/**
 * Weapons, armour and arrows. Like tools, these are pack items (generated into ITEMS);
 * equipping moves one into an equipment slot. Bonuses feed the combat formulas in combat.ts.
 */
import type { SkillId } from './skills.js';

/** Arrows have slot 'ammo' but aren't worn: like tools, the best arrows in your pack are used. */
export type Slot = 'weapon' | 'ammo' | 'head' | 'body' | 'legs' | 'ring' | 'neck';
export type WornSlot = Exclude<Slot, 'ammo'>;
export const SLOTS: WornSlot[] = ['weapon', 'head', 'body', 'legs', 'neck', 'ring'];
/** Jewellery perks: worn rings and amulets. */
export type Perk = 'prospector' | 'smelter' | 'hearty' | 'keen' | 'thrifty' | 'radiant';
export const PERKS: Record<Perk, { name: string; desc: string }> = {
  prospector: { name: 'Prospector', desc: 'Find gems and curios more often while mining' },
  smelter: { name: 'Smelter', desc: 'Sometimes a smelt keeps its ore' },
  hearty: { name: 'Hearty', desc: '+20 maximum hitpoints' },
  keen: { name: 'Keen', desc: '+6 accuracy and strength' },
  thrifty: { name: 'Thrifty', desc: 'Sometimes an anvil job takes one bar fewer' },
  radiant: { name: 'Radiant', desc: '+30 maximum hitpoints' },
};
export type CombatStyle = 'melee' | 'ranged' | 'magic';
/** Weapon specials: spend special energy for a stronger attack. */
export type SpecialId = 'lunge' | 'cleave' | 'volley' | 'quake';
export type WeaponModel = 'sword' | 'battleaxe' | 'maul' | 'shortbow' | 'longbow';

export interface GearDef {
  name: string;
  slot: Slot;
  /** Level needed to equip. */
  req: [SkillId, number];
  /** Accuracy and strength bonus (weapons, arrows), defence bonus (armour). */
  acc?: number;
  str?: number;
  def?: number;
  /** Leather armour: accuracy that only counts with a bow. */
  racc?: number;
  /** Weapons: ticks between attacks, reach in tiles, style, special. */
  speed?: number;
  range?: number;
  style?: CombatStyle;
  special?: SpecialId;
  model?: WeaponModel;
  /** 1 bronze, 2 iron, 3 starsilver, 4 heartwood, 5 wood, 6 steel, 7 deepglass, 8 emberheart, 9 silver, 10 gold,
   *  11 cobalt, 12 titanium, 13 tungsten, 14 black iron, 15 iridium, 16 old-world alloy, 17 platinum;
   *  sewn: 18 leather, 19 furred, 20 studded, 21 wolfhide, 22 boarhide, 23 lurkerhide, 24 chitin, 25 wraithweave, 26 moonhide. */
  metal: Metal;
  /** Jewellery: what wearing it does. */
  perk?: Perk;
  /** Quality from the anvil (none = Rough) and wear; set on generated variants. */
  grade?: Grade;
  wear?: WearState;
  /** Broken gear gives nothing until it's repaired. */
  broken?: boolean;
  /** The plain item this is a variant of. */
  base?: BaseGearId;
  /** Coins in a shop, if sold. */
  price?: number;
  /** Arrows stack. */
  stack?: boolean;
  desc: string;
}

export type Metal = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 | 26;
/** Smithed metals: these come in qualities and wear out. Value is a rough worth per piece. */
export const SMITHED: Partial<Record<Metal, { name: string; durability: number }>> = {
  1: { name: 'bronze', durability: 1500 }, 2: { name: 'iron', durability: 2500 }, 6: { name: 'steel', durability: 3500 },
  3: { name: 'starsilver', durability: 5000 }, 7: { name: 'deepglass', durability: 6500 }, 8: { name: 'emberheart', durability: 8000 },
  11: { name: 'cobalt', durability: 9000 }, 12: { name: 'titanium', durability: 9500 }, 13: { name: 'tungsten', durability: 10500 },
  14: { name: 'black iron', durability: 11500 }, 15: { name: 'iridium', durability: 13000 }, 16: { name: 'old-world alloy', durability: 12000 },
};
const W = (name: string, req: [SkillId, number], acc: number, str: number, speed: number, model: WeaponModel, metal: GearDef['metal'], special: SpecialId, desc: string, price?: number): GearDef =>
  ({ name, slot: 'weapon', req, acc, str, speed, range: model.endsWith('bow') ? (model === 'longbow' ? 8 : 6) : 1, style: model.endsWith('bow') ? 'ranged' : 'melee', special, model, metal, price, desc });
const A = (name: string, slot: 'head' | 'body' | 'legs', lvl: number, def: number, metal: GearDef['metal'], price?: number): GearDef =>
  ({ name, slot, req: ['defence', lvl], def, metal, price, desc: `+${def} defence.` });
/** Sewn leather (Crafting): less defence than metal, but a ranger's accuracy. Needs Ranged to wear. */
const LA = (name: string, slot: 'head' | 'body' | 'legs', lvl: number, def: number, racc: number, metal: GearDef['metal'], price?: number): GearDef =>
  ({ name, slot, req: ['ranged', lvl], def, racc, metal, price, desc: `+${def} defence, +${racc} ranged accuracy.` });
const AM = (name: string, lvl: number, str: number, metal: GearDef['metal'], price?: number): GearDef =>
  ({ name, slot: 'ammo', req: ['ranged', lvl], str, metal, price, stack: true, desc: `+${str} ranged strength. One per shot.` });
const J = (name: string, slot: 'ring' | 'neck', lvl: number, bonus: Partial<Pick<GearDef, 'acc' | 'str' | 'def'>>, metal: 9 | 10 | 17, perk?: Perk, desc = ''): GearDef =>
  ({ name, slot, req: ['defence', lvl], ...bonus, metal, perk, desc: desc || [bonus.acc ? `+${bonus.acc} accuracy` : '', bonus.str ? `+${bonus.str} strength` : '', bonus.def ? `+${bonus.def} defence` : ''].filter(Boolean).join(', ') + '.' });

const BASE_GEAR = {
  bronze_sword: W('Bronze sword', ['melee', 1], 8, 7, 4, 'sword', 1, 'lunge', 'Quick and honest. Special: Lunge, a sure hit.', 40),
  bronze_battleaxe: W('Bronze battleaxe', ['melee', 5], 4, 12, 5, 'battleaxe', 1, 'cleave', 'Slow and heavy. Special: Cleave, a crushing blow.', 70),
  iron_sword: W('Iron sword', ['melee', 10], 16, 14, 4, 'sword', 2, 'lunge', 'Special: Lunge, a sure hit.', 260),
  iron_battleaxe: W('Iron battleaxe', ['melee', 15], 10, 22, 5, 'battleaxe', 2, 'cleave', 'Special: Cleave, a crushing blow.', 420),
  steel_sword: W('Steel sword', ['melee', 20], 24, 22, 4, 'sword', 6, 'lunge', 'Holds an edge all day. Special: Lunge.'),
  steel_battleaxe: W('Steel battleaxe', ['melee', 24], 16, 33, 5, 'battleaxe', 6, 'cleave', 'Special: Cleave.'),
  star_sword: W('Starsilver sword', ['melee', 30], 32, 30, 4, 'sword', 3, 'lunge', 'Light as a reed. Special: Lunge.'),
  star_battleaxe: W('Starsilver battleaxe', ['melee', 35], 22, 44, 5, 'battleaxe', 3, 'cleave', 'Special: Cleave.'),
  deep_sword: W('Deepglass sword', ['melee', 42], 42, 40, 4, 'sword', 7, 'lunge', 'Dark, and it drinks the light. Special: Lunge.'),
  deep_battleaxe: W('Deepglass battleaxe', ['melee', 44], 30, 56, 5, 'battleaxe', 7, 'cleave', 'Special: Cleave.'),
  ember_sword: W('Emberheart sword', ['melee', 48], 50, 48, 4, 'sword', 8, 'lunge', 'Warm to the touch, always. Special: Lunge.'),
  ember_battleaxe: W('Emberheart battleaxe', ['melee', 50], 36, 68, 5, 'battleaxe', 8, 'cleave', 'Special: Cleave.'),
  heartwood_maul: W('Heartwood maul', ['melee', 40], 38, 60, 5, 'maul', 4, 'quake', 'Grown, not forged. Special: Quake, a huge blow that stuns.'),
  pine_shortbow: W('Pine shortbow', ['ranged', 1], 8, 0, 4, 'shortbow', 5, 'volley', 'Special: Volley, two arrows at once.', 35),
  oak_shortbow: W('Oak shortbow', ['ranged', 5], 14, 0, 4, 'shortbow', 5, 'volley', 'Special: Volley.', 120),
  oak_longbow: W('Oak longbow', ['ranged', 20], 24, 0, 5, 'longbow', 5, 'volley', 'Long reach. Special: Volley.', 480),
  ironwood_longbow: W('Ironwood longbow', ['ranged', 35], 36, 0, 5, 'longbow', 2, 'volley', 'Special: Volley.'),
  heartwood_bow: W('Heartwood bow', ['ranged', 40], 46, 0, 4, 'longbow', 4, 'volley', 'It draws itself. Special: Volley.'),
  ash_longbow: W('Ash longbow', ['ranged', 55], 58, 0, 5, 'longbow', 5, 'volley', 'Straight-grained and springy. Special: Volley.'),
  cedar_longbow: W('Cedar longbow', ['ranged', 60], 64, 0, 5, 'longbow', 5, 'volley', 'Smells of the cove. Special: Volley.'),
  ghostoak_longbow: W('Ghost oak longbow', ['ranged', 65], 70, 0, 5, 'longbow', 5, 'volley', 'Pale as bone. Special: Volley.'),
  stormwood_longbow: W('Stormwood longbow', ['ranged', 70], 76, 0, 5, 'longbow', 5, 'volley', 'It hums in a wind. Special: Volley.'),
  blackwood_longbow: W('Blackwood longbow', ['ranged', 75], 82, 0, 5, 'longbow', 5, 'volley', 'Hard to see at night, and so is the arrow. Special: Volley.'),
  redwood_longbow: W('Redwood longbow', ['ranged', 80], 88, 0, 5, 'longbow', 5, 'volley', 'Tall as you are. Special: Volley.'),
  glasswood_longbow: W('Glasswood longbow', ['ranged', 85], 94, 0, 5, 'longbow', 5, 'volley', 'It glows faintly when drawn. Special: Volley.'),
  ancientyew_longbow: W('Ancient yew longbow', ['ranged', 90], 100, 0, 5, 'longbow', 5, 'volley', 'Older than anyone who will ever draw it. Special: Volley.'),
  worldroot_longbow: W('Worldroot bow', ['ranged', 95], 108, 0, 4, 'longbow', 5, 'volley', 'Grown, not carved. It draws itself. Special: Volley.'),
  bronze_arrows: AM('Bronze arrows', 1, 7, 1, 2),
  iron_arrows: AM('Iron arrows', 10, 14, 2, 5),
  steel_arrows: AM('Steel arrows', 20, 21, 6),
  star_arrows: AM('Starsilver arrows', 30, 28, 3),
  deep_arrows: AM('Deepglass arrows', 42, 36, 7),
  ember_arrows: AM('Emberheart arrows', 48, 44, 8),
  bronze_helm: A('Bronze helm', 'head', 1, 3, 1, 45),
  bronze_body: A('Bronze body', 'body', 1, 8, 1, 110),
  bronze_legs: A('Bronze legs', 'legs', 1, 5, 1, 70),
  iron_helm: A('Iron helm', 'head', 10, 6, 2, 260),
  iron_body: A('Iron body', 'body', 10, 16, 2, 640),
  iron_legs: A('Iron legs', 'legs', 10, 10, 2, 420),
  steel_helm: A('Steel helm', 'head', 20, 9, 6),
  steel_body: A('Steel body', 'body', 20, 24, 6),
  steel_legs: A('Steel legs', 'legs', 20, 15, 6),
  star_helm: A('Starsilver helm', 'head', 30, 12, 3),
  star_body: A('Starsilver body', 'body', 30, 32, 3),
  star_legs: A('Starsilver legs', 'legs', 30, 20, 3),
  deep_helm: A('Deepglass helm', 'head', 42, 15, 7),
  deep_body: A('Deepglass body', 'body', 42, 40, 7),
  deep_legs: A('Deepglass legs', 'legs', 42, 25, 7),
  ember_helm: A('Emberheart helm', 'head', 48, 18, 8),
  ember_body: A('Emberheart body', 'body', 48, 48, 8),
  ember_legs: A('Emberheart legs', 'legs', 48, 30, 8),
  // the deep metals, cobalt to iridium (each needs that much Melee, Defence or Ranged)
  cobalt_sword: W('Cobalt sword', ['melee', 55], 58, 56, 4, 'sword', 11, 'lunge', 'Blue-black, and it never quite warms. Special: Lunge.'),
  cobalt_battleaxe: W('Cobalt battleaxe', ['melee', 55], 41, 78, 5, 'battleaxe', 11, 'cleave', 'Special: Cleave.'),
  cobalt_arrows: AM('Cobalt arrows', 55, 50, 11),
  cobalt_helm: A('Cobalt helm', 'head', 55, 21, 11),
  cobalt_body: A('Cobalt body', 'body', 55, 56, 11),
  cobalt_legs: A('Cobalt legs', 'legs', 55, 35, 11),
  titanium_sword: W('Titanium sword', ['melee', 60], 64, 62, 4, 'sword', 12, 'lunge', 'Cut from an aircraft, and light as one. Special: Lunge.'),
  titanium_battleaxe: W('Titanium battleaxe', ['melee', 60], 46, 86, 5, 'battleaxe', 12, 'cleave', 'Special: Cleave.'),
  titanium_arrows: AM('Titanium arrows', 60, 56, 12),
  titanium_helm: A('Titanium helm', 'head', 60, 24, 12),
  titanium_body: A('Titanium body', 'body', 60, 64, 12),
  titanium_legs: A('Titanium legs', 'legs', 60, 40, 12),
  tungsten_sword: W('Tungsten sword', ['melee', 65], 70, 68, 4, 'sword', 13, 'lunge', 'Heavy enough to end an argument. Special: Lunge.'),
  tungsten_battleaxe: W('Tungsten battleaxe', ['melee', 65], 51, 94, 5, 'battleaxe', 13, 'cleave', 'Special: Cleave.'),
  tungsten_arrows: AM('Tungsten arrows', 65, 62, 13),
  tungsten_helm: A('Tungsten helm', 'head', 65, 27, 13),
  tungsten_body: A('Tungsten body', 'body', 65, 72, 13),
  tungsten_legs: A('Tungsten legs', 'legs', 65, 45, 13),
  alloy_sword: W('Old-world alloy sword', ['melee', 70], 76, 74, 4, 'sword', 16, 'lunge', 'Nobody alive knows how to make the metal, only how to shape it. Special: Lunge.'),
  alloy_battleaxe: W('Old-world alloy battleaxe', ['melee', 70], 56, 102, 5, 'battleaxe', 16, 'cleave', 'Special: Cleave.'),
  alloy_arrows: AM('Old-world alloy arrows', 70, 68, 16),
  alloy_helm: A('Old-world alloy helm', 'head', 70, 30, 16),
  alloy_body: A('Old-world alloy body', 'body', 70, 80, 16),
  alloy_legs: A('Old-world alloy legs', 'legs', 70, 50, 16),
  blackiron_sword: W('Black iron sword', ['melee', 80], 84, 82, 4, 'sword', 14, 'lunge', 'It drinks the light. Special: Lunge.'),
  blackiron_battleaxe: W('Black iron battleaxe', ['melee', 80], 62, 112, 5, 'battleaxe', 14, 'cleave', 'Special: Cleave.'),
  blackiron_arrows: AM('Black iron arrows', 80, 76, 14),
  blackiron_helm: A('Black iron helm', 'head', 80, 34, 14),
  blackiron_body: A('Black iron body', 'body', 80, 90, 14),
  blackiron_legs: A('Black iron legs', 'legs', 80, 56, 14),
  iridium_sword: W('Iridium sword', ['melee', 90], 94, 92, 4, 'sword', 15, 'lunge', 'There is nothing it will not cut. Special: Lunge.'),
  iridium_battleaxe: W('Iridium battleaxe', ['melee', 90], 70, 126, 5, 'battleaxe', 15, 'cleave', 'Special: Cleave.'),
  iridium_arrows: AM('Iridium arrows', 90, 86, 15),
  iridium_helm: A('Iridium helm', 'head', 90, 39, 15),
  iridium_body: A('Iridium body', 'body', 90, 102, 15),
  iridium_legs: A('Iridium legs', 'legs', 90, 64, 15),
  iridium_maul: W('Iridium maul', ['melee', 95], 84, 140, 5, 'maul', 15, 'quake', 'Iridium round an old-world alloy core: the heaviest thing a smith can make. Special: Quake, a huge blow that stuns.'),
  bark_helm: A('Heartwood bark helm', 'head', 40, 15, 4),
  bark_body: A('Heartwood bark body', 'body', 40, 40, 4),
  bark_legs: A('Heartwood bark legs', 'legs', 40, 25, 4),
  // sewn leather, from Crafting: [name, Ranged to wear, defence head/body/legs, ranged accuracy head/body/legs]
  leather_coif: LA('Leather coif', 'head', 1, 2, 1, 18, 30),
  leather_body: LA('Leather body', 'body', 1, 6, 3, 18, 75),
  leather_chaps: LA('Leather chaps', 'legs', 1, 4, 2, 18, 55),
  furred_coif: LA('Furred leather coif', 'head', 10, 4, 2, 19, 50),
  furred_body: LA('Furred leather body', 'body', 10, 10, 4, 19, 120),
  furred_chaps: LA('Furred leather chaps', 'legs', 10, 7, 3, 19, 75),
  studded_coif: LA('Studded leather coif', 'head', 20, 6, 3, 20, 65),
  studded_body: LA('Studded leather body', 'body', 20, 15, 6, 20, 175),
  studded_chaps: LA('Studded leather chaps', 'legs', 20, 10, 4, 20, 120),
  wolfhide_coif: LA('Wolfhide coif', 'head', 30, 9, 4, 21, 75),
  wolfhide_body: LA('Wolfhide body', 'body', 30, 22, 8, 21, 210),
  wolfhide_chaps: LA('Wolfhide chaps', 'legs', 30, 14, 5, 21, 145),
  boarhide_coif: LA('Boarhide coif', 'head', 40, 12, 5, 22, 115),
  boarhide_body: LA('Boarhide body', 'body', 40, 28, 10, 22, 325),
  boarhide_chaps: LA('Boarhide chaps', 'legs', 40, 18, 7, 22, 220),
  lurker_coif: LA('Lurkerhide coif', 'head', 52, 15, 6, 23, 280),
  lurker_body: LA('Lurkerhide body', 'body', 52, 36, 13, 23, 820),
  lurker_chaps: LA('Lurkerhide chaps', 'legs', 52, 23, 9, 23, 550),
  chitin_coif: LA('Chitin coif', 'head', 64, 19, 8, 24, 670),
  chitin_body: LA('Chitin body', 'body', 64, 45, 16, 24, 1720),
  chitin_chaps: LA('Chitin chaps', 'legs', 64, 29, 11, 24, 1060),
  wraith_coif: LA('Wraithweave coif', 'head', 76, 23, 10, 25, 695),
  wraith_body: LA('Wraithweave body', 'body', 76, 55, 20, 25, 2050),
  wraith_chaps: LA('Wraithweave chaps', 'legs', 76, 35, 13, 25, 1370),
  moon_hood: LA('Moonhide hood', 'head', 88, 27, 14, 26, 9000),
  // more bows (Forestry, at the fletching bench): these want a bowstring from the spinning wheel
  silverbark_longbow: W('Silverbark longbow', ['ranged', 40], 42, 0, 5, 'longbow', 5, 'volley', 'Pale and light, and it shoots straight in the dark. Special: Volley.'),
  yew_longbow: W('Yew longbow', ['ranged', 48], 52, 0, 5, 'longbow', 5, 'volley', 'The old bow of the old wars. Special: Volley.'),
  // charms: curios strung on wool (Crafting)
  bead_necklace: J('Glass bead necklace', 'neck', 1, { acc: 1, def: 1 }, 9, undefined, '+1 accuracy, +1 defence.'),
  lucky_charm: J("Rabbit's foot charm", 'neck', 1, { acc: 2, def: 2 }, 9),
  brass_charm: J('Brass charm necklace', 'neck', 1, { acc: 3, str: 2 }, 10),
  pearl_amulet: J('Bog pearl amulet', 'neck', 1, { str: 3, def: 4 }, 9),
  fossil_charm: J('Fossil charm', 'neck', 1, { def: 7 }, 17),
  feather_charm: J('Golden feather charm', 'neck', 1, { acc: 7, def: 2 }, 10),
  cavegem_amulet: J('Cave gem amulet', 'neck', 1, { acc: 6, str: 6, def: 2 }, 9),
  frost_amulet: J('Frost shard amulet', 'neck', 1, { acc: 7, str: 7, def: 5 }, 17),
  // jewellery: cast at the furnace, set with cut gems
  silver_ring: J('Silver ring', 'ring', 1, { def: 2 }, 9),
  silver_amulet: J('Silver amulet', 'neck', 1, { acc: 3 }, 9),
  sapphire_ring: J('Sapphire ring', 'ring', 1, { def: 2 }, 9, 'prospector', '+2 defence. Prospector: find gems and curios more often while mining.'),
  emerald_ring: J('Emerald ring', 'ring', 1, { def: 2 }, 9, 'smelter', '+2 defence. Smelter: sometimes a smelt keeps its ore.'),
  gold_ring: J('Gold ring', 'ring', 1, { def: 4, acc: 2 }, 10),
  gold_amulet: J('Gold amulet', 'neck', 1, { str: 4 }, 10),
  ruby_amulet: J('Ruby amulet', 'neck', 1, { str: 3 }, 10, 'hearty', '+3 strength. Hearty: +20 maximum hitpoints.'),
  diamond_amulet: J('Diamond amulet', 'neck', 1, { acc: 6, str: 6 }, 10, 'keen', 'Keen: +6 accuracy and strength.'),
  platinum_ring: J('Platinum ring', 'ring', 1, { def: 6, acc: 4 }, 17),
  platinum_amulet: J('Platinum amulet', 'neck', 1, { str: 7, acc: 4 }, 17),
  glowstone_ring: J('Glowstone ring', 'ring', 1, { def: 5 }, 17, 'thrifty', '+5 defence. Thrifty: sometimes an anvil job takes one bar fewer.'),
  glowstone_amulet: J('Glowstone amulet', 'neck', 1, { acc: 8, str: 8 }, 17, 'radiant', '+8 accuracy and strength. Radiant: +30 maximum hitpoints.'),
} satisfies Record<string, GearDef>;

export type BaseGearId = keyof typeof BASE_GEAR;
export type Grade = 'fine' | 'superior' | 'master';
export type WearState = 'worn' | 'broken';
export const GRADES: Grade[] = ['fine', 'superior', 'master'];
export const GRADE_NAMES: Record<Grade, string> = { fine: 'Fine', superior: 'Superior', master: 'Masterwork' };
const GRADE_MULT: Record<Grade, number> = { fine: 1.08, superior: 1.16, master: 1.25 };
const GRADE_VALUE: Record<Grade, number> = { fine: 1.3, superior: 1.7, master: 2.6 };
/** Quality and wear ride on the item id: "iron_sword+fine", "iron_pick+worn", "steel_body+master+broken". */
export type VariantMod = Grade | WearState | `${Grade}+${WearState}`;
export type GearId = BaseGearId | `${BaseGearId}+${VariantMod}`;

/** Split an id into its plain item, quality and wear. */
export function parseVariant(id: string): { base: string; grade: Grade | null; wear: WearState | null } {
  const [base, ...mods] = id.split('+');
  return { base, grade: (mods.find(m => GRADES.includes(m as Grade)) as Grade) ?? null, wear: (mods.find(m => m === 'worn' || m === 'broken') as WearState) ?? null };
}
export function variantId(base: string, grade: Grade | null, wear: WearState | null) { return [base, grade, wear].filter(Boolean).join('+'); }
/** Prefix and suffix for a variant's name. */
export function variantName(name: string, grade: Grade | null, wear: WearState | null) {
  const n = grade ? `${GRADE_NAMES[grade]} ${name.charAt(0).toLowerCase()}${name.slice(1)}` : name;
  return wear ? `${n} (${wear})` : n;
}

const GEAR_TABLE: Record<string, GearDef> = { ...BASE_GEAR };
for (const [id, g] of Object.entries(BASE_GEAR) as [BaseGearId, GearDef][]) {
  if (!SMITHED[g.metal] || g.slot === 'ammo') continue;
  for (const grade of [null, ...GRADES]) for (const wear of [null, 'worn', 'broken'] as (WearState | null)[]) {
    if (!grade && !wear) continue;
    const m = (grade ? GRADE_MULT[grade] : 1) * (wear === 'worn' ? 0.8 : 1), up = (v?: number) => (v ? (wear === 'broken' ? 0 : Math.max(v + (grade ? 1 : 0), Math.round(v * m))) : v);
    GEAR_TABLE[variantId(id, grade, wear)] = {
      ...g, name: variantName(g.name, grade, wear), acc: up(g.acc), str: up(g.str), def: up(g.def), base: id,
      ...(grade ? { grade } : {}), ...(wear ? { wear } : {}), ...(wear === 'broken' ? { broken: true } : {}),
      price: undefined,
      desc: wear === 'broken' ? 'Broken. Bram can repair it, or repair it yourself at an anvil with a bar of the same metal.' : g.desc,
    };
  }
}
export const GEAR = GEAR_TABLE as Record<GearId, GearDef>;
export const GEAR_IDS = Object.keys(GEAR) as GearId[];
export const isGearId = (v: unknown): v is GearId => typeof v === 'string' && Object.prototype.hasOwnProperty.call(GEAR, v);
/** Things that aren't weapons but can be swung (wield.ts fills this in: a hatchet, a log…). */
let improvisedFallback: ((id: string) => GearDef | null) | null = null;
export const setImprovised = (f: (id: string) => GearDef | null) => { improvisedFallback = f; };
export const gearDef = (id: GearId): GearDef => GEAR[id] ?? improvisedFallback?.(id) ?? (GEAR[id] as GearDef);
const METAL_WORTH: Record<Metal, number> = { 1: 60, 2: 300, 3: 1600, 4: 4000, 5: 200, 6: 700, 7: 3200, 8: 5200, 9: 400, 10: 1100, 11: 6500, 12: 7200, 13: 8200, 14: 9500, 15: 12000, 16: 10500, 17: 4000, 18: 60, 19: 140, 20: 300, 21: 700, 22: 1000, 23: 2000, 24: 3200, 25: 5200, 26: 9000 };
/** What a general trader pays for gear. */
export function gearValue(d: GearDef) {
  const base = d.base ? BASE_GEAR[d.base] as GearDef : d;
  const v = (base.price ?? METAL_WORTH[d.metal]) * 0.4 / (d.stack ? 10 : 1) * (d.grade ? GRADE_VALUE[d.grade] : 1) * (d.wear === 'worn' ? 0.6 : d.wear === 'broken' ? 0.3 : 1);
  return Math.max(1, Math.round(v));
}

export const SPECIALS: Record<SpecialId, { name: string; cost: number; desc: string }> = {
  lunge: { name: 'Lunge', cost: 50, desc: 'A sure hit for 1.4× damage.' },
  cleave: { name: 'Cleave', cost: 60, desc: 'A crushing blow for 1.8× damage.' },
  volley: { name: 'Volley', cost: 50, desc: 'Loose two arrows at once.' },
  quake: { name: 'Quake', cost: 75, desc: '2× damage, and the target is stunned for 3 ticks.' },
};

/** Melee and ranged stances: "attack" trains the weapon skill, "guard" trains Defence and defends better. */
export type Stance = 'attack' | 'guard';
